import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { MultiSelect } from "@/components/MultiSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { SistemistiReportTabs } from "@/components/SistemistiReportTabs";
import { toast } from "sonner";
import { FunnelIcon as Funnel, DownloadSimpleIcon as DownloadSimple, ToolboxIcon as Toolbox, CaretUpIcon as CaretUp, CaretDownIcon as CaretDown, PencilSimpleIcon as PencilSimple, ArrowCounterClockwiseIcon as ArrowCounterClockwise, WarningCircleIcon as WarningCircle, PlusIcon as Plus, ArrowClockwiseIcon as ArrowClockwise, ScalesIcon as Scales } from "@phosphor-icons/react";

const MESI = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const nfEuro = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
const nfDec = new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const euro = (v) => (v == null || Number.isNaN(v) || !Number.isFinite(v) ? "—" : nfEuro.format(v));
const dec2 = (v) => (v == null || Number.isNaN(v) || !Number.isFinite(v) ? "—" : nfDec.format(v));
const pct = (v) => (v == null || Number.isNaN(v) || !Number.isFinite(v) ? "—" : `${nfDec.format(v)}%`);
const intg = (v) => (v == null ? "—" : String(v));
const dec4 = (v) => (v == null || Number.isNaN(v) || !Number.isFinite(v) ? "—" : new Intl.NumberFormat("it-IT", { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(v));
const margineOf = (r) => (r.risultati != null && r.fatturabile != null ? r.risultati - r.fatturabile : null);
const euroColor = (v) => (v == null ? "" : v > 0 ? "text-emerald-600" : v < 0 ? "text-rose-600" : "");
const STATO_PREMIO_LBL = { in_maturazione: "In maturazione", maturato: "Maturato", congelato: "Congelato", sbloccato_semestre: "Sbloccato a semestre", sbloccato_fine_anno: "Sbloccato a fine anno", assegnato: "Assegnato", erogato: "Erogato" };

const COL_DEFS = [
  { k: "mese_nome", l: "Mese", align: "left", w: 100, fmt: (r) => r.mese_nome, multiOnly: true },
  { k: "tecnico", l: "Tecnico", align: "left", w: 150, fmt: (r) => r.tecnico },
  { k: "peso_ula", l: "Peso ULA", w: 90, fmt: (r) => dec2(r.peso_ula) },
  { k: "target_mese", l: "Target mese AC", w: 130, fmt: (r) => euro(r.target_mese) },
  { k: "prezzo_orario_atteso", l: "Prezzo orario medio atteso", w: 160, fmt: (r) => euro(r.prezzo_orario_atteso) },
  { k: "giorni_lavorativi", l: "Giorni lavorativi AC", w: 130, fmt: (r) => intg(r.giorni_lavorativi) },
  { k: "giorni_lavorati", l: "Giorni lavorati AC", w: 140, fmt: null },
  { k: "prezzo_orario_reale", l: "Prezzo orario medio reale AC", w: 170, fmt: (r) => euro(r.prezzo_orario_reale) },
  { k: "fatturabile", l: "Fatturabile AC", w: 130, fmt: (r) => euro(r.fatturabile) },
  { k: "media_straordinari", l: "Media straordinari giorno AC", w: 170, fmt: (r) => dec2(r.media_straordinari) },
  { k: "rendimento_pct", l: "Rendimento % AC", w: 130, fmt: null },
  { k: "risultati", l: "Risultati AC", w: 120, fmt: (r) => euro(r.risultati) },
  { k: "margine_azienda", l: "Margine Azienda", w: 140, fmt: null },
  { k: "score", l: "Score premio", w: 110, fmt: (r) => dec4(r.score) },
  { k: "premio_calcolato", l: "Premio calcolato", w: 130, fmt: (r) => euro(r.premio_calcolato) },
  { k: "premio_cap", l: "Premio calcolato CAP", w: 150, fmt: (r) => euro(r.premio_cap) },
  { k: "premio_assegnato", l: "Premio assegnato", w: 170, fmt: null },
  { k: "stato_premio", l: "Stato premio", w: 120, fmt: (r) => STATO_PREMIO_LBL[r.stato_premio] || r.stato_premio },
];
const DEFAULT_W = Object.fromEntries(COL_DEFS.map((c) => [c.k, c.w]));
const DEFAULT_LABELS = Object.fromEntries(COL_DEFS.map((c) => [c.k, c.l]));
const COL_W_KEY = "sistemisti_perf_col_widths";
const COL_LABELS_KEY = "sistemisti_perf_col_labels";
const COL_HIDDEN_KEY = "sistemisti_perf_col_hidden";
const FILTERS_KEY = "sistemisti_perf_filters";
const loadFilters = () => { try { return JSON.parse(localStorage.getItem(FILTERS_KEY)) || {}; } catch { return {}; } };
const SIDEBAR_W_KEY = "sistemisti_perf_sidebar_w";
const DEFAULT_SIDEBAR_W = 256;

export default function SistemistiReport() {
  const now = new Date();
  const savedFilters = loadFilters();
  const [anni, setAnni] = useState([now.getFullYear()]);
  const [anno, setAnno] = useState(savedFilters.anno || now.getFullYear());
  const [selMesi, setSelMesi] = useState(Array.isArray(savedFilters.mesi) && savedFilters.mesi.length ? savedFilters.mesi : [now.getMonth() + 1]);
  const [tecnici, setTecnici] = useState([]);
  const [selTec, setSelTec] = useState(Array.isArray(savedFilters.tecnici) ? savedFilters.tecnici : []);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ key: "tecnico", dir: "asc" });
  const [ovr, setOvr] = useState(null);
  const [ovrVal, setOvrVal] = useState("");
  const [ovrNote, setOvrNote] = useState("");
  const [ass, setAss] = useState(null);
  const [assVal, setAssVal] = useState("");
  const [assNote, setAssNote] = useState("");
  // nuovo anno
  const [nyOpen, setNyOpen] = useState(false);
  const [nyAnno, setNyAnno] = useState(now.getFullYear() + 1);
  const [nyMode, setNyMode] = useState("vuoto");
  const [nySrc, setNySrc] = useState(now.getFullYear());
  // confronto versione bloccata
  const [confronto, setConfronto] = useState(null);
  // ridimensionamento
  const [sidebarW, setSidebarW] = useState(() => { const v = parseInt(localStorage.getItem(SIDEBAR_W_KEY), 10); return v && v >= 200 && v <= 480 ? v : DEFAULT_SIDEBAR_W; });
  const [colW, setColW] = useState(() => { try { return { ...DEFAULT_W, ...(JSON.parse(localStorage.getItem(COL_W_KEY)) || {}) }; } catch { return { ...DEFAULT_W }; } });
  const [colLabels, setColLabels] = useState(() => { try { return { ...(JSON.parse(localStorage.getItem(COL_LABELS_KEY)) || {}) }; } catch { return {}; } });
  const [colHidden, setColHidden] = useState(() => { try { return { ...(JSON.parse(localStorage.getItem(COL_HIDDEN_KEY)) || {}) }; } catch { return {}; } });
  const [rnOpen, setRnOpen] = useState(false);
  const [rnDraft, setRnDraft] = useState({});
  const [rnHidden, setRnHidden] = useState({});
  const labelOf = useCallback((c) => (colLabels[c.k] && colLabels[c.k].trim()) || c.l, [colLabels]);

  useEffect(() => { api.get("/tecnici").then(({ data }) => setTecnici(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/reportistica/sistemisti/anni").then(({ data }) => { setAnni(data.anni); if (!data.anni.includes(anno)) setAnno(data.anni[0]); }).catch(() => {}); /* eslint-disable-next-line */ }, []);
  useEffect(() => { localStorage.setItem(SIDEBAR_W_KEY, String(sidebarW)); }, [sidebarW]);
  useEffect(() => { localStorage.setItem(COL_W_KEY, JSON.stringify(colW)); }, [colW]);
  useEffect(() => { localStorage.setItem(COL_LABELS_KEY, JSON.stringify(colLabels)); }, [colLabels]);
  useEffect(() => { localStorage.setItem(COL_HIDDEN_KEY, JSON.stringify(colHidden)); }, [colHidden]);
  useEffect(() => { localStorage.setItem(FILTERS_KEY, JSON.stringify({ anno, mesi: selMesi, tecnici: selTec })); }, [anno, selMesi, selTec]);

  const load = useCallback(() => {
    if (!selMesi.length) { setData(null); return; }
    setLoading(true);
    const q = new URLSearchParams({ anno, mesi: [...selMesi].sort((a, b) => a - b).join(",") });
    if (selTec.length) q.set("tecnico_ids", selTec.join(","));
    api.get(`/reportistica/sistemisti/performance?${q}`).then(({ data }) => setData(data))
      .catch(() => toast.error("Errore caricamento performance")).finally(() => setLoading(false));
  }, [anno, selMesi, selTec]);
  useEffect(() => { load(); }, [load]);

  const multi = (data?.multi_mese) || selMesi.length > 1;
  const cols = useMemo(() => COL_DEFS.filter((c) => (!c.multiOnly || multi) && !(colHidden[c.k] && c.k !== "tecnico")), [multi, colHidden]);
  const openRename = () => { setRnDraft({ ...colLabels }); setRnHidden({ ...colHidden }); setRnOpen(true); };
  const saveRename = () => { setColLabels({ ...rnDraft }); setColHidden({ ...rnHidden }); setRnOpen(false); };
  const resetRename = () => { setRnDraft({}); setRnHidden({}); };

  const rows = useMemo(() => {
    const rs = [...(data?.rows || [])];
    const { key, dir } = sort;
    rs.sort((a, b) => {
      // in multi-mese ordina sempre prima per mese
      if (multi && a.mese !== b.mese) return a.mese - b.mese;
      let x = a[key], y = b[key];
      if (typeof x === "string") { x = x.toLowerCase(); y = (y || "").toLowerCase(); }
      if (x == null) x = -Infinity; if (y == null) y = -Infinity;
      return (x < y ? -1 : x > y ? 1 : 0) * (dir === "asc" ? 1 : -1);
    });
    return rs;
  }, [data, sort, multi]);

  const toggleSort = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));

  // avviso periodi bloccati con differenza dai dati correnti
  const bloccatiDiff = useMemo(() => {
    const pm = data?.per_mese || {};
    return Object.values(pm).filter((m) => m.bloccato && m.differisce_da_bloccato);
  }, [data]);

  const riepilogoGap = useMemo(() => {
    const totMargine = rows.reduce((s, r) => s + (margineOf(r) ?? 0), 0);
    const totPremi = rows.reduce((s, r) => s + (r.premio_assegnato ?? 0), 0);
    return { totMargine, totPremi, gapNetto: totMargine - totPremi };
  }, [rows]);

  const premiBudget = useMemo(() => {
    const pm = data?.per_mese || {};
    const budget = Object.values(pm).reduce((s, m) => s + (m.budget_premi_mese || 0), 0);
    const used = Object.values(pm).reduce((s, m) => s + (m.premi_assegnati_mese || 0), 0);
    const perc = budget > 0 ? Math.round((used / budget) * 1000) / 10 : (used > 0 ? 100 : 0);
    return { budget, used, residuo: budget - used, perc, over: used > budget + 0.001 ? used - budget : 0 };
  }, [data]);

  const saveOvr = async () => {
    const v = parseInt(ovrVal, 10);
    if (Number.isNaN(v) || v < 0) return toast.error("Valore non valido (>= 0)");
    if (!ovrNote.trim()) return toast.error("La motivazione è obbligatoria");
    try {
      await api.put("/reportistica/sistemisti/giorni-override", {
        tecnico_id: ovr.tecnico_id, anno, mese: ovr.mese, valore_manuale: v,
        valore_automatico: ovr.giorni_lavorati_auto, motivazione: ovrNote.trim() });
      toast.success("Giorni lavorati aggiornati"); setOvr(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio override"); }
  };
  const resetOvr = async (row) => {
    if (!window.confirm(`Ripristinare i giorni automatici per ${row.tecnico} (${row.mese_nome})?`)) return;
    try {
      await api.delete(`/reportistica/sistemisti/giorni-override?tecnico_id=${row.tecnico_id}&anno=${anno}&mese=${row.mese}`);
      toast.success("Giorni automatici ripristinati"); setOvr(null); load();
    } catch { toast.error("Errore ripristino"); }
  };

  const saveAss = async () => {
    const v = Number(String(assVal).replace(",", "."));
    if (Number.isNaN(v) || v < 0) return toast.error("Valore non valido (>= 0)");
    if (data?.cap_mensile != null && v > data.cap_mensile + 0.001) return toast.error(`Il premio non può superare il cap mensile (${euro(data.cap_mensile)})`);
    if (!assNote.trim()) return toast.error("La motivazione è obbligatoria");
    try {
      await api.put("/reportistica/sistemisti/premi/assegnazione", { anno, mese: ass.mese, tecnico_id: ass.tecnico_id, valore_assegnato: v, motivazione: assNote.trim() });
      toast.success("Premio assegnato salvato"); setAss(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };
  const resetAss = async (row) => {
    if (!window.confirm(`Ripristinare il premio automatico per ${row.tecnico} (${row.mese_nome})?`)) return;
    try {
      await api.delete(`/reportistica/sistemisti/premi/assegnazione?anno=${anno}&mese=${row.mese}&tecnico_id=${row.tecnico_id}`);
      toast.success("Premio automatico ripristinato"); setAss(null); load();
    } catch { toast.error("Errore ripristino"); }
  };

  const createYear = async () => {
    const y = parseInt(nyAnno, 10);
    if (Number.isNaN(y) || y < 2000 || y > 2100) return toast.error("Anno non valido");
    try {
      await api.post("/reportistica/sistemisti/anni", { anno: y, modalita: nyMode, anno_sorgente: nyMode === "copia" ? Number(nySrc) : undefined });
      toast.success(`Anno ${y} creato`);
      const { data } = await api.get("/reportistica/sistemisti/anni");
      setAnni(data.anni); setAnno(y); setNyOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione anno"); }
  };

  const openConfronto = async (trimestre) => {
    try {
      const { data } = await api.get(`/reportistica/sistemisti/premi/versioni/confronto?anno=${anno}&trimestre=${trimestre}`);
      setConfronto(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore confronto"); }
  };

  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = sidebarW;
    const onMove = (ev) => setSidebarW(Math.max(200, Math.min(480, startW + (ev.clientX - startX))));
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); document.body.style.userSelect = ""; };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
  };
  const startColResize = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX, startW = colW[key] || DEFAULT_W[key] || 120;
    const onMove = (ev) => setColW((w) => ({ ...w, [key]: Math.max(60, Math.min(500, startW + (ev.clientX - startX))) }));
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); document.body.style.userSelect = ""; };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
  };

  const exportCsv = () => {
    const head = cols.map((c) => labelOf(c)).join(";");
    const cell = (r, k) => {
      switch (k) {
        case "mese_nome": return r.mese_nome; case "tecnico": return r.tecnico; case "peso_ula": return dec2(r.peso_ula);
        case "target_mese": return euro(r.target_mese); case "prezzo_orario_atteso": return euro(r.prezzo_orario_atteso);
        case "giorni_lavorativi": return intg(r.giorni_lavorativi); case "giorni_lavorati": return intg(r.giorni_lavorati);
        case "prezzo_orario_reale": return euro(r.prezzo_orario_reale); case "fatturabile": return euro(r.fatturabile);
        case "media_straordinari": return dec2(r.media_straordinari); case "rendimento_pct": return pct(r.rendimento_pct);
        case "risultati": return euro(r.risultati); case "score": return dec4(r.score);
        case "margine_azienda": return euro(margineOf(r));
        case "premio_calcolato": return euro(r.premio_calcolato); case "premio_cap": return euro(r.premio_cap);
        case "premio_assegnato": return euro(r.premio_assegnato); case "stato_premio": return STATO_PREMIO_LBL[r.stato_premio] || r.stato_premio;
        default: return "";
      }
    };
    const lines = rows.map((r) => cols.map((c) => `"${cell(r, c.k)}"`).join(";"));
    const csv = "\ufeff" + [head, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `performance_sistemisti_${anno}_${selMesi.join("-")}.csv`;
    a.click();
  };

  const isBlockedMonth = (mese) => (data?.per_mese?.[String(mese)]?.bloccato) || false;

  return (
    <InternalLayout>
      <div className="p-6 lg:p-8">
        <PageHeader title="Performance Tecnici" subtitle="Sistemisti · rendimento e valorizzazione economica per tecnico" />
        <SistemistiReportTabs />

        <div className="flex flex-col lg:flex-row gap-0 lg:gap-0">
          {/* Filtri sidebar (ridimensionabile) */}
          <aside className="w-full lg:flex-shrink-0" style={typeof window !== "undefined" && window.innerWidth >= 1024 ? { width: sidebarW } : undefined}>
            <Card className="rounded-sm border-border shadow-none p-4 space-y-3" data-testid="sistemisti-filters">
              <div className="flex items-center gap-1.5 pb-1"><Funnel size={16} className="text-primary" weight="fill" /><h3 className="font-heading text-sm font-semibold">Filtri</h3></div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Anno</Label>
                <div className="flex items-center gap-1.5 mt-1">
                  <Select value={String(anno)} onValueChange={(v) => setAnno(Number(v))}>
                    <SelectTrigger className="rounded-sm h-9 flex-1" data-testid="filter-anno"><SelectValue /></SelectTrigger>
                    <SelectContent>{anni.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" className="rounded-sm h-9 w-9 flex-shrink-0" onClick={() => { setNyAnno((anni[0] || now.getFullYear()) + 1); setNySrc(anno); setNyOpen(true); }} title="Nuovo anno" data-testid="new-year-btn"><Plus size={16} /></Button>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Mese</Label>
                <div className="mt-1"><MultiSelect width="w-full" testid="filter-mesi" placeholder="Seleziona mesi" value={selMesi.map(String)} onChange={(v) => setSelMesi(v.map(Number))} searchable options={MESI.slice(1).map((m, i) => ({ value: String(i + 1), label: m }))} /></div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Tecnico</Label>
                <div className="mt-1"><MultiSelect width="w-full" testid="filter-tecnico" placeholder="Tutti i tecnici" value={selTec} onChange={setSelTec} searchable options={tecnici.map((t) => ({ value: t.id, label: t.name }))} /></div>
              </div>
              <Button variant="outline" className="w-full rounded-sm h-9 gap-1.5" onClick={exportCsv} disabled={!rows.length} data-testid="export-sistemisti"><DownloadSimple size={15} /> Esporta (Excel/CSV)</Button>
              <div className="pt-1 flex flex-col gap-1 border-t border-border/60">
                <button className="text-[11px] text-muted-foreground hover:text-primary text-left flex items-center gap-1" onClick={openRename} data-testid="manage-columns-btn"><PencilSimple size={11} /> Gestione colonne (rinomina / nascondi)</button>
                <button className="text-[11px] text-muted-foreground hover:text-primary text-left flex items-center gap-1" onClick={() => setSidebarW(DEFAULT_SIDEBAR_W)} data-testid="reset-sidebar-w"><ArrowClockwise size={11} /> Ripristina larghezza pannello</button>
                <button className="text-[11px] text-muted-foreground hover:text-primary text-left flex items-center gap-1" onClick={() => setColW({ ...DEFAULT_W })} data-testid="reset-col-widths"><ArrowClockwise size={11} /> Ripristina larghezze colonne</button>
              </div>
            </Card>
          </aside>
          {/* Maniglia ridimensionamento pannello (desktop) */}
          <div className="hidden lg:flex items-stretch w-3 cursor-col-resize group flex-shrink-0" onMouseDown={startResize} data-testid="sidebar-resize-handle" title="Trascina per ridimensionare">
            <div className="w-0.5 mx-auto rounded-full bg-border group-hover:bg-primary transition-colors" />
          </div>

          {/* Contenuto */}
          <div className="flex-1 min-w-0">
            {data && (data.per_mese) && (
              <Card className="rounded-sm border-border shadow-none p-4 mb-4 max-w-md" data-testid="premi-budget-box">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Premi Performance · budget periodo {selMesi.length > 1 ? "(mesi selezionati)" : ""}</p>
                <div className="flex items-end justify-between mt-1">
                  <div><span className="text-lg font-semibold">{euro(premiBudget.used)}</span><span className="text-xs text-muted-foreground"> / {euro(premiBudget.budget)}</span></div>
                  <span className={`text-sm font-medium ${premiBudget.over > 0 ? "text-rose-600" : "text-muted-foreground"}`}>{premiBudget.perc}%</span>
                </div>
                <div className="h-1.5 bg-secondary rounded-full mt-2 overflow-hidden"><div className={`h-full ${premiBudget.over > 0 ? "bg-rose-500" : "bg-primary"}`} style={{ width: `${Math.min(100, premiBudget.perc)}%` }} /></div>
                {premiBudget.over > 0
                  ? <p className="text-xs mt-1 text-rose-600 font-medium flex items-center gap-1"><WarningCircle size={12} weight="fill" /> Superamento: {euro(premiBudget.over)}</p>
                  : <p className="text-xs mt-1 text-muted-foreground">Residuo: {euro(premiBudget.residuo)}</p>}
              </Card>
            )}
            {data?.warnings?.length > 0 && (
              <div className="mb-4 rounded-sm bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3 space-y-1" data-testid="sistemisti-warnings">
                {data.warnings.map((w, i) => <div key={i} className="flex items-center gap-2"><WarningCircle size={15} weight="fill" /> {w}</div>)}
              </div>
            )}
            {bloccatiDiff.length > 0 && (
              <div className="mb-4 rounded-sm bg-blue-50 border border-blue-200 text-blue-900 text-sm p-3" data-testid="blocked-banner">
                <div className="flex items-center gap-2 font-medium"><WarningCircle size={15} weight="fill" /> I dati correnti differiscono dalla versione bloccata del trimestre.</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[...new Set(bloccatiDiff.map((m) => m.trimestre))].map((tr) => (
                    <Button key={tr} size="sm" variant="outline" className="rounded-sm h-7 gap-1 text-xs" onClick={() => openConfronto(tr)} data-testid={`confronto-btn-${tr}`}><Scales size={13} /> Confronta con versione bloccata (Q{tr})</Button>
                  ))}
                </div>
              </div>
            )}
            <Card className="rounded-sm border-border shadow-none overflow-hidden">
              <div className="overflow-x-auto">
                <table className="text-xs" style={{ tableLayout: "fixed", width: cols.reduce((s, c) => s + (colW[c.k] || 120), 0) }} data-testid="sistemisti-table">
                  <colgroup>{cols.map((c) => <col key={c.k} style={{ width: colW[c.k] || 120 }} />)}</colgroup>
                  <thead>
                    <tr className="text-left border-b border-border bg-secondary/40">
                      {cols.map((c) => (
                        <th key={c.k} className={`relative px-3 py-2.5 font-semibold select-none ${c.align === "left" ? "text-left" : "text-right"}`} data-testid={`col-${c.k}`}>
                          <span className="inline-flex items-center gap-1 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap max-w-full align-middle" onClick={() => toggleSort(c.k)} title={labelOf(c)}>{labelOf(c)}{sort.key === c.k && (sort.dir === "asc" ? <CaretUp size={11} /> : <CaretDown size={11} />)}</span>
                          <span className="absolute top-0 right-0 h-full w-2 cursor-col-resize hover:bg-primary/30" onMouseDown={(e) => startColResize(e, c.k)} data-testid={`col-resize-${c.k}`} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-muted-foreground">Caricamento…</td></tr>}
                    {!loading && rows.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-muted-foreground">Nessun dato per il periodo selezionato.</td></tr>}
                    {!loading && rows.map((r) => (
                      <tr key={`${r.mese}-${r.tecnico_id}`} className="border-b border-border/40 hover:bg-secondary/20" data-testid={`row-${r.mese}-${r.tecnico_id}`}>
                        {cols.map((c) => {
                          if (c.k === "mese_nome") return <td key={c.k} className="px-3 py-2 font-medium truncate">{r.mese_nome}</td>;
                          if (c.k === "tecnico") return <td key={c.k} className="px-3 py-2 font-medium truncate" title={r.tecnico}>{r.tecnico}</td>;
                          if (c.k === "rendimento_pct") return <td key={c.k} className="px-3 py-2 text-right tabular-nums font-semibold truncate" style={r.rendimento_colore ? { color: r.rendimento_colore } : {}} data-testid={`rend-${r.mese}-${r.tecnico_id}`}>{pct(r.rendimento_pct)}</td>;
                          if (c.k === "giorni_lavorati") return (
                            <td key={c.k} className="px-3 py-2 text-right tabular-nums">
                              <span className="inline-flex items-center gap-1.5 justify-end">
                                <span title={r.giorni_override_manuale ? `Manuale (auto: ${r.giorni_lavorati_auto})${r.giorni_override_motivazione ? " · " + r.giorni_override_motivazione : ""}` : ""}>{intg(r.giorni_lavorati)}</span>
                                {r.giorni_override_manuale
                                  ? <span className="text-[9px] px-1 rounded bg-amber-100 text-amber-700" data-testid={`ovr-badge-${r.mese}-${r.tecnico_id}`}>Manuale</span>
                                  : <span className="text-[9px] px-1 rounded bg-zinc-100 text-zinc-500">Auto</span>}
                                <button className="text-muted-foreground hover:text-primary" onClick={() => { setOvr(r); setOvrVal(String(r.giorni_lavorati)); setOvrNote(r.giorni_override_motivazione || ""); }} data-testid={`ovr-edit-${r.mese}-${r.tecnico_id}`}><PencilSimple size={12} /></button>
                                {r.giorni_override_manuale && <button className="text-muted-foreground hover:text-primary" onClick={() => resetOvr(r)} data-testid={`ovr-reset-${r.mese}-${r.tecnico_id}`} title="Ripristina automatico"><ArrowCounterClockwise size={12} /></button>}
                              </span>
                            </td>
                          );
                          if (c.k === "premio_assegnato") return (
                            <td key={c.k} className="px-3 py-2 text-right tabular-nums">
                              <span className="inline-flex items-center gap-1.5 justify-end">
                                <span className={r.premio_is_override ? "font-semibold" : ""}>{euro(r.premio_assegnato)}</span>
                                {r.premio_is_override
                                  ? <span className="text-[9px] px-1 rounded bg-amber-100 text-amber-700" title={`Automatico: ${euro(r.premio_valore_automatico ?? r.premio_cap)}${r.premio_motivazione ? " · " + r.premio_motivazione : ""}`} data-testid={`ass-badge-${r.mese}-${r.tecnico_id}`}>Manuale</span>
                                  : <span className="text-[9px] px-1 rounded bg-zinc-100 text-zinc-500">Auto</span>}
                                <button className="text-muted-foreground hover:text-primary" onClick={() => { setAss(r); setAssVal(String(r.premio_assegnato ?? "")); setAssNote(r.premio_is_override ? (r.premio_motivazione || "") : ""); }} data-testid={`ass-edit-${r.mese}-${r.tecnico_id}`}><PencilSimple size={12} /></button>
                                {r.premio_is_override && <button className="text-muted-foreground hover:text-primary" onClick={() => resetAss(r)} data-testid={`ass-reset-${r.mese}-${r.tecnico_id}`} title="Ripristina automatico"><ArrowCounterClockwise size={12} /></button>}
                              </span>
                            </td>
                          );
                          if (c.k === "stato_premio") return <td key={c.k} className="px-3 py-2 text-right"><span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{STATO_PREMIO_LBL[r.stato_premio] || r.stato_premio}</span></td>;
                          if (c.k === "margine_azienda") { const m = margineOf(r); return <td key={c.k} className={`px-3 py-2 text-right tabular-nums truncate font-medium ${euroColor(m)}`} title="Risultati − Fatturabile" data-testid={`margine-${r.mese}-${r.tecnico_id}`}>{euro(m)}</td>; }
                          return <td key={c.k} className={`px-3 py-2 tabular-nums truncate ${c.align === "left" ? "text-left" : "text-right"}`}>{c.fmt ? c.fmt(r) : intg(r[c.k])}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            {rows.length > 0 && (
              <Card className="rounded-sm border-border shadow-none mt-4 p-4" data-testid="gap-summary">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Riepilogo GAP — periodi e tecnici selezionati</p>
                <div className="space-y-1.5 max-w-xl">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">GAP totale (Margine Azienda) <span className="text-[11px]">= Σ (Risultati − Fatturabile)</span></span>
                    <b className={`tabular-nums text-base ${euroColor(riepilogoGap.totMargine)}`} data-testid="gap-totale">{euro(riepilogoGap.totMargine)}</b>
                  </div>
                  <div className="flex items-center justify-between text-sm border-t border-border/60 pt-1.5">
                    <span className="text-muted-foreground">GAP al netto dei premi erogati <span className="text-[11px]">= GAP totale − premi assegnati ({euro(riepilogoGap.totPremi)})</span></span>
                    <b className={`tabular-nums text-base ${euroColor(riepilogoGap.gapNetto)}`} data-testid="gap-netto">{euro(riepilogoGap.gapNetto)}</b>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Dialog override giorni */}
        <Dialog open={!!ovr} onOpenChange={(o) => !o && setOvr(null)}>
          <DialogContent className="rounded-sm max-w-sm" data-testid="ovr-dialog">
            <DialogHeader><DialogTitle>Giorni lavorati — {ovr?.tecnico}</DialogTitle>
              <DialogDescription>{ovr?.mese_nome} {anno}{isBlockedMonth(ovr?.mese) ? " · trimestre bloccato" : ""}</DialogDescription></DialogHeader>
            <div className="space-y-3">
              {isBlockedMonth(ovr?.mese) && <div className="rounded-sm bg-blue-50 border border-blue-200 text-blue-800 text-xs p-2">La modifica NON altera la versione bloccata attiva: aggiorna i dati correnti (usati per una nuova versione).</div>}
              <div className="flex items-center justify-between text-sm bg-secondary/40 rounded-sm px-3 py-2">
                <span className="text-muted-foreground">Valore automatico calcolato</span><b>{ovr?.giorni_lavorati_auto} giorni</b>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Nuovo valore manuale (giorni)</Label>
                <Input type="number" min={0} value={ovrVal} onChange={(e) => setOvrVal(e.target.value)} className="mt-1 rounded-sm" data-testid="ovr-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Motivazione <span className="text-rose-500">*</span></Label>
                <Textarea value={ovrNote} onChange={(e) => setOvrNote(e.target.value)} rows={2} className="mt-1 rounded-sm" placeholder="Obbligatoria" data-testid="ovr-note" />
              </div>
            </div>
            <DialogFooter>
              {ovr?.giorni_override_manuale && <Button variant="outline" className="rounded-sm mr-auto gap-1.5" onClick={() => resetOvr(ovr)} data-testid="ovr-dialog-reset"><ArrowCounterClockwise size={14} /> Ripristina automatico</Button>}
              <Button variant="outline" className="rounded-sm" onClick={() => setOvr(null)}>Annulla</Button>
              <Button className="rounded-sm" onClick={saveOvr} data-testid="ovr-save">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog premio assegnato */}
        <Dialog open={!!ass} onOpenChange={(o) => !o && setAss(null)}>
          <DialogContent className="rounded-sm max-w-md" data-testid="ass-dialog">
            <DialogHeader><DialogTitle>Premio assegnato — {ass?.tecnico}</DialogTitle>
              <DialogDescription>{ass?.mese_nome} {anno}{isBlockedMonth(ass?.mese) ? " · trimestre bloccato" : ""}</DialogDescription></DialogHeader>
            <div className="space-y-3">
              {isBlockedMonth(ass?.mese) && <div className="rounded-sm bg-blue-50 border border-blue-200 text-blue-800 text-xs p-2">La modifica NON altera la versione bloccata attiva: aggiorna i dati correnti (usati per una nuova versione).</div>}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-secondary/40 rounded-sm px-3 py-2"><div className="text-[11px] text-muted-foreground">Premio calcolato CAP</div><b>{euro(ass?.premio_valore_automatico ?? ass?.premio_cap)}</b></div>
                <div className="bg-secondary/40 rounded-sm px-3 py-2"><div className="text-[11px] text-muted-foreground">Assegnato attuale</div><b>{euro(ass?.premio_assegnato)}</b></div>
              </div>
              <p className="text-[11px] text-muted-foreground">Cap mensile per tecnico: <b>{euro(data?.cap_mensile)}</b>.</p>
              <div>
                <Label className="text-xs uppercase tracking-wider">Nuovo premio assegnato (€) <span className="text-rose-500">*</span></Label>
                <Input value={assVal} onChange={(e) => setAssVal(e.target.value)} className="mt-1 rounded-sm" data-testid="ass-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Motivazione <span className="text-rose-500">*</span></Label>
                <Textarea value={assNote} onChange={(e) => setAssNote(e.target.value)} rows={2} className="mt-1 rounded-sm" placeholder="Obbligatoria" data-testid="ass-note" />
              </div>
            </div>
            <DialogFooter>
              {ass?.premio_is_override && <Button variant="outline" className="rounded-sm mr-auto gap-1.5" onClick={() => resetAss(ass)} data-testid="ass-dialog-reset"><ArrowCounterClockwise size={14} /> Ripristina automatico</Button>}
              <Button variant="outline" className="rounded-sm" onClick={() => setAss(null)}>Annulla</Button>
              <Button className="rounded-sm" onClick={saveAss} data-testid="ass-save">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog nuovo anno */}
        <Dialog open={nyOpen} onOpenChange={setNyOpen}>
          <DialogContent className="rounded-sm max-w-sm" data-testid="new-year-dialog">
            <DialogHeader><DialogTitle>Nuovo anno</DialogTitle><DialogDescription>Crea configurazione Sistemisti e Premi per un nuovo anno.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs uppercase tracking-wider">Anno da creare</Label>
                <Input type="number" value={nyAnno} onChange={(e) => setNyAnno(e.target.value)} className="mt-1 rounded-sm" data-testid="ny-anno" /></div>
              <div><Label className="text-xs uppercase tracking-wider">Modalità</Label>
                <Select value={nyMode} onValueChange={setNyMode}>
                  <SelectTrigger className="mt-1 rounded-sm h-9" data-testid="ny-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vuoto">Crea configurazione vuota</SelectItem>
                    <SelectItem value="copia">Copia configurazione da un altro anno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {nyMode === "copia" && <div><Label className="text-xs uppercase tracking-wider">Anno sorgente</Label>
                <Select value={String(nySrc)} onValueChange={(v) => setNySrc(Number(v))}>
                  <SelectTrigger className="mt-1 rounded-sm h-9" data-testid="ny-src"><SelectValue /></SelectTrigger>
                  <SelectContent>{anni.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Copia ULA, prezzi, giorni, tecnici e budget. NON copia giorni lavorati, premi assegnati, versioni o dati economici.</p></div>}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setNyOpen(false)}>Annulla</Button>
              <Button className="rounded-sm" onClick={createYear} data-testid="ny-save">Crea anno</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog gestione colonne (rinomina / nascondi) */}
        <Dialog open={rnOpen} onOpenChange={setRnOpen}>
          <DialogContent className="rounded-sm max-w-md" data-testid="manage-columns-dialog">
            <DialogHeader><DialogTitle>Gestione colonne</DialogTitle>
              <DialogDescription>Rinomina o nascondi le colonne (solo a livello di visualizzazione, salvato nel browser).</DialogDescription></DialogHeader>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {COL_DEFS.map((c) => (
                <div key={c.k} className="flex items-center gap-2" data-testid={`mc-row-${c.k}`}>
                  <Checkbox checked={!(rnHidden[c.k]) || c.k === "tecnico"} disabled={c.k === "tecnico"}
                    onCheckedChange={(v) => setRnHidden((h) => ({ ...h, [c.k]: !v }))} data-testid={`mc-visible-${c.k}`} />
                  <Input value={rnDraft[c.k] ?? ""} placeholder={c.l} onChange={(e) => setRnDraft((d) => ({ ...d, [c.k]: e.target.value }))} className="rounded-sm h-8 text-sm flex-1" data-testid={`mc-label-${c.k}`} />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm mr-auto" onClick={resetRename} data-testid="mc-reset">Ripristina default</Button>
              <Button variant="outline" className="rounded-sm" onClick={() => setRnOpen(false)}>Annulla</Button>
              <Button className="rounded-sm" onClick={saveRename} data-testid="mc-save">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog confronto versioni */}
        <Dialog open={!!confronto} onOpenChange={(o) => !o && setConfronto(null)}>
          <DialogContent className="rounded-sm max-w-5xl" data-testid="confronto-dialog">
            <DialogHeader><DialogTitle>Confronto versioni — Q{confronto?.trimestre} {confronto?.anno}</DialogTitle>
              <DialogDescription>{confronto?.base?.label} vs {confronto?.target?.label}</DialogDescription></DialogHeader>
            {confronto && (
              <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  {[["Budget", "budget"], ["Premi CAP", "cap"], ["Assegnati", "assegnato"], ["Residuo", "residuo"]].map(([lbl, k]) => (
                    <div key={k} className="bg-secondary/40 rounded-sm px-3 py-2">
                      <div className="text-[11px] text-muted-foreground">{lbl}</div>
                      <div className="flex items-center justify-between"><span>{confronto.base.label}</span><b>{euro(confronto.riepilogo[`${k}_base`])}</b></div>
                      <div className="flex items-center justify-between"><span>{confronto.target.label}</span><b>{euro(confronto.riepilogo[`${k}_target`])}</b></div>
                      {confronto.riepilogo[`diff_${k}`] != null && (k === "cap" || k === "assegnato" || k === "budget") && (
                        <div className={`text-xs mt-1 font-semibold ${confronto.riepilogo[`diff_${k}`] > 0 ? "text-emerald-600" : confronto.riepilogo[`diff_${k}`] < 0 ? "text-rose-600" : "text-muted-foreground"}`}>
                          {confronto.riepilogo[`diff_${k}`] > 0 ? "+" : ""}{euro(confronto.riepilogo[`diff_${k}`])}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="overflow-x-auto border border-border rounded-sm">
                  <table className="w-full text-xs whitespace-nowrap">
                    <thead><tr className="text-left border-b border-border bg-secondary/40">
                      {["Mese", "Tecnico", "GG (base→nuovo)", "Rend. (base→nuovo)", "Score", "CAP (base→nuovo)", "Δ CAP", "Assegnato (base→nuovo)", "Δ Assegnato"].map((h) => <th key={h} className="px-2 py-2 font-semibold">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {confronto.righe.map((r, i) => (
                        <tr key={i} className={`border-b border-border/40 ${!r.presente_base ? "bg-emerald-50" : !r.presente_target ? "bg-rose-50" : ""}`}>
                          <td className="px-2 py-1.5">{r.mese_nome}</td>
                          <td className="px-2 py-1.5 font-medium">{r.tecnico}{!r.presente_base && <span className="ml-1 text-[9px] text-emerald-600">nuova</span>}{!r.presente_target && <span className="ml-1 text-[9px] text-rose-600">rimossa</span>}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{intg(r.giorni_base)} → {intg(r.giorni_target)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{pct(r.rendimento_base)} → {pct(r.rendimento_target)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{dec4(r.score_base)} → {dec4(r.score_target)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{euro(r.cap_base)} → {euro(r.cap_target)}</td>
                          <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${r.diff_cap > 0 ? "text-emerald-600" : r.diff_cap < 0 ? "text-rose-600" : "text-muted-foreground"}`}>{r.diff_cap > 0 ? "+" : ""}{euro(r.diff_cap)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{euro(r.assegnato_base)} → {euro(r.assegnato_target)}</td>
                          <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${r.diff_assegnato > 0 ? "text-emerald-600" : r.diff_assegnato < 0 ? "text-rose-600" : "text-muted-foreground"}`}>{r.diff_assegnato > 0 ? "+" : ""}{euro(r.diff_assegnato)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-muted-foreground">Nessun pagamento o compensazione automatica. La differenza è solo informativa per valutazione manuale.</p>
              </div>
            )}
            <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setConfronto(null)}>Chiudi</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </InternalLayout>
  );
}
